import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MessageCircle, Plus, Search, Upload } from 'lucide-react'
import { MagicLinkSendModal } from '@/components/MagicLinkSendModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import {
  InlineEditCell,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/auth'
import { cleanRut, formatRut, isValidRut } from '@/features/workers/rut'
import {
  useCreateWorker,
  useOrganizations,
  useUpdateWorker,
  useWorkers,
} from '@/features/workers/hooks'
import type { Worker } from '@/features/workers/types'

type SortKey = 'rut' | 'givenName' | 'familyName' | 'organizationName' | 'createdAt'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 50

export default function WorkersPage() {
  const { user } = useAuth()
  const { show } = useToast()
  const isDemographicsOnly = user?.role === 'recepcionista'
  // F7 — magic link send modal trigger desde la fila trabajador.
  const [magicLinkWorker, setMagicLinkWorker] = useState<Worker | null>(null)

  const [search, setSearch] = useState('')
  const [organizationId, setOrganizationId] = useState<string | undefined>()
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'familyName',
    dir: 'asc',
  })
  const [offset, setOffset] = useState(0)

  const { data: orgsData } = useOrganizations()
  const organizations = orgsData?.items ?? []

  const { data, isLoading, isError, error, refetch } = useWorkers({
    search,
    organizationId,
    limit: PAGE_SIZE,
    offset,
  })

  const updateWorker = useUpdateWorker()
  const createWorker = useCreateWorker()

  const sorted = useMemo(() => {
    if (!data?.items) return []
    const arr = [...data.items]
    arr.sort((a, b) => {
      const av = (a[sort.key] ?? '') as string
      const bv = (b[sort.key] ?? '') as string
      const cmp = av.localeCompare(bv, 'es', { sensitivity: 'base' })
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [data, sort])

  const orgOptions = [
    { value: '', label: 'Todas las empresas' },
    ...organizations.map((o) => ({ value: o.id, label: o.name })),
  ]

  const onSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    )
  }

  const handleInlineUpdate = async (
    worker: Worker,
    field: keyof Worker,
    value: string,
  ): Promise<void> => {
    const previous = worker[field]
    if (previous === value) return

    try {
      await updateWorker.mutateAsync({ id: worker.id, patch: { [field]: value } })
      show({
        kind: 'success',
        title: 'Cambio guardado',
        description: 'La fila se actualizó correctamente.',
        duration: 3000,
        action: {
          label: 'Deshacer',
          onClick: () => {
            updateWorker.mutate({ id: worker.id, patch: { [field]: previous } })
          },
        },
      })
    } catch {
      show({
        kind: 'error',
        title: 'No se pudo guardar',
        description: 'Revisa tu conexión e intenta nuevamente.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="workers-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Trabajadores</h1>
          <p className="text-sm text-slate-600">
            {isDemographicsOnly
              ? 'Consulta de datos demográficos (sin fichas clínicas).'
              : 'Gestiona la nómina de trabajadores evaluables por empresa contratante.'}
          </p>
        </div>
        {!isDemographicsOnly && user?.role === 'admin' && (
          <div className="flex gap-2">
            <Link to="/app/workers/bulk-upload">
              <Button
                variant="outline"
                leftIcon={<Upload className="h-4 w-4" />}
                data-testid="open-bulk-upload"
              >
                Cargar Excel
              </Button>
            </Link>
          </div>
        )}
      </header>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="w-full sm:w-96">
          <Input
            leftIcon={<Search className="h-4 w-4" />}
            placeholder="Buscar por RUT o nombre"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
            data-testid="workers-search"
          />
        </div>
        <div className="w-full sm:w-64">
          <Select
            value={organizationId ?? ''}
            onChange={(v) => {
              setOrganizationId(v || undefined)
              setOffset(0)
            }}
            options={orgOptions}
            placeholder="Filtrar por empresa"
            data-testid="workers-org-filter"
          />
        </div>
      </div>

      {isError && (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 p-3 text-sm text-[color:var(--color-fitlab-red-hover)]"
        >
          No se pudieron cargar los trabajadores: {error?.message ?? 'error desconocido'}.{' '}
          <button type="button" onClick={() => void refetch()} className="font-semibold underline">
            Reintentar
          </button>
        </div>
      )}

      <TableContainer className="max-h-[calc(100dvh-16rem)]">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDir={sort.key === 'rut' ? sort.dir : null}
                onSort={() => onSort('rut')}
              >
                RUT
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDir={sort.key === 'givenName' ? sort.dir : null}
                onSort={() => onSort('givenName')}
              >
                Nombres
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDir={sort.key === 'familyName' ? sort.dir : null}
                onSort={() => onSort('familyName')}
              >
                Apellidos
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDir={sort.key === 'organizationName' ? sort.dir : null}
                onSort={() => onSort('organizationName')}
              >
                Empresa
              </TableHeaderCell>
              <TableHeaderCell>Contacto</TableHeaderCell>
              <TableHeaderCell>Acción</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows rows={8} cols={6} />
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <TableEmpty>
                    <p className="font-medium text-slate-700">No hay trabajadores registrados.</p>
                    {user?.role === 'admin' && (
                      <p>
                        Usa la opción <strong>Cargar Excel</strong> para subir la primera nómina.
                      </p>
                    )}
                  </TableEmpty>
                </td>
              </tr>
            ) : (
              sorted.map((worker) => (
                <TableRow key={worker.id} data-testid={`worker-row-${worker.id}`}>
                  <TableCell className="font-mono text-xs">
                    <InlineEditCell
                      value={worker.rut}
                      disabled={isDemographicsOnly}
                      formatDisplay={(v) => formatRut(v)}
                      data-testid={`cell-rut-${worker.id}`}
                      onCommit={async (next) => {
                        const normalized = cleanRut(next)
                        if (!isValidRut(normalized)) {
                          show({
                            kind: 'error',
                            title: 'RUT inválido',
                            description: 'El dígito verificador no coincide.',
                          })
                          return
                        }
                        await handleInlineUpdate(worker, 'rut', normalized)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <InlineEditCell
                      value={worker.givenName}
                      disabled={isDemographicsOnly}
                      onCommit={(v) => handleInlineUpdate(worker, 'givenName', v)}
                    />
                  </TableCell>
                  <TableCell>
                    <InlineEditCell
                      value={worker.familyName}
                      disabled={isDemographicsOnly}
                      onCommit={(v) => handleInlineUpdate(worker, 'familyName', v)}
                    />
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {worker.organizationName ??
                      organizations.find((o) => o.id === worker.organizationId)?.name ??
                      '—'}
                  </TableCell>
                  <TableCell>
                    <InlineEditCell
                      value={worker.phone ?? ''}
                      placeholder="+56 9 ..."
                      disabled={isDemographicsOnly}
                      onCommit={(v) => handleInlineUpdate(worker, 'phone', v)}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setMagicLinkWorker(worker)}
                      disabled={!worker.phone || isDemographicsOnly}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-fitlab-blue)]/30 bg-[color:var(--color-fitlab-blue)]/5 px-3 text-xs font-medium text-[color:var(--color-fitlab-blue-hover)] hover:bg-[color:var(--color-fitlab-blue)]/10 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid={`btn-send-form-${worker.id}`}
                      title={
                        worker.phone ? 'Enviar formulario por WhatsApp' : 'Trabajador sin teléfono'
                      }
                    >
                      <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                      Enviar formulario
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}

            {/* Inline create row (solo admin, no recepcionista) */}
            {!isDemographicsOnly && user?.role === 'admin' && (
              <InlineCreateRow
                organizationId={organizationId ?? organizations[0]?.id}
                onCreate={async (payload) => {
                  try {
                    await createWorker.mutateAsync(payload)
                    show({ kind: 'success', title: 'Trabajador creado' })
                  } catch {
                    show({
                      kind: 'error',
                      title: 'No se pudo crear',
                      description: 'Revisa RUT y empresa.',
                    })
                  }
                }}
                organizations={organizations}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && data.total > PAGE_SIZE && (
        <Pagination total={data.total} offset={offset} pageSize={PAGE_SIZE} onChange={setOffset} />
      )}

      {/* F7 — Modal Magic Link disparado desde botón "Enviar formulario" */}
      {magicLinkWorker && (
        <MagicLinkSendModal
          patientId={magicLinkWorker.id}
          patientName={magicLinkWorker.givenName ?? 'trabajador'}
          patientPhoneLast4={(magicLinkWorker.phone ?? '').replace(/\D/g, '').slice(-4)}
          purpose="form_fill"
          open={!!magicLinkWorker}
          onClose={() => setMagicLinkWorker(null)}
        />
      )}
    </div>
  )
}

function InlineCreateRow({
  organizationId,
  organizations,
  onCreate,
}: {
  organizationId?: string
  organizations: Array<{ id: string; name: string }>
  onCreate: (payload: {
    rut: string
    givenName: string
    familyName: string
    organizationId: string
  }) => Promise<void>
}) {
  const [rut, setRut] = useState('')
  const [given, setGiven] = useState('')
  const [family, setFamily] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canSubmit =
    rut.length > 0 &&
    given.trim().length > 0 &&
    family.trim().length > 0 &&
    organizationId !== undefined &&
    isValidRut(rut)

  const submit = async () => {
    if (!canSubmit || !organizationId) return
    setSubmitting(true)
    try {
      await onCreate({
        rut: cleanRut(rut),
        givenName: given.trim(),
        familyName: family.trim(),
        organizationId,
      })
      setRut('')
      setGiven('')
      setFamily('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <tr className="bg-slate-50/50" data-testid="worker-create-row">
      <TableCell>
        <input
          className="h-8 w-full rounded border border-slate-300 bg-white px-2 font-mono text-xs focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-fitlab-blue)]"
          value={rut}
          onChange={(e) => setRut(e.target.value)}
          placeholder="12345678-9"
          aria-invalid={rut.length > 0 && !isValidRut(rut)}
          data-testid="create-rut"
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </TableCell>
      <TableCell>
        <input
          className="h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-fitlab-blue)]"
          value={given}
          onChange={(e) => setGiven(e.target.value)}
          placeholder="Nombre"
          data-testid="create-given"
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </TableCell>
      <TableCell>
        <input
          className="h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm focus:border-[color:var(--color-fitlab-blue)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-fitlab-blue)]"
          value={family}
          onChange={(e) => setFamily(e.target.value)}
          placeholder="Apellido"
          data-testid="create-family"
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </TableCell>
      <TableCell className="text-xs text-slate-500">
        {organizations.find((o) => o.id === organizationId)?.name ?? '—'}
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          variant="outline"
          onClick={submit}
          loading={submitting}
          disabled={!canSubmit}
          leftIcon={<Plus className="h-3.5 w-3.5" />}
          data-testid="create-submit"
        >
          Agregar
        </Button>
      </TableCell>
    </tr>
  )
}

function Pagination({
  total,
  offset,
  pageSize,
  onChange,
}: {
  total: number
  offset: number
  pageSize: number
  onChange: (next: number) => void
}) {
  const page = Math.floor(offset / pageSize) + 1
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  return (
    <nav
      className="flex items-center justify-between text-sm text-slate-600"
      aria-label="Paginación"
    >
      <span>
        Página <strong>{page}</strong> de <strong>{lastPage}</strong> ({total} trabajadores)
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - pageSize))}
        >
          Anterior
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={offset + pageSize >= total}
          onClick={() => onChange(offset + pageSize)}
        >
          Siguiente
        </Button>
      </div>
    </nav>
  )
}
